import React from 'react';
import { useDrop } from 'react-aria';
import { Icon } from '@iconify/react';
import { Button, Card, ProgressBar, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { DroppedItem } from '../../uploadIntake';

/** The bar under the file: rows so far against the total, or no measure yet. */
export type ChosenFileProgress =
    | { kind: 'indeterminate'; label: string }
    | { kind: 'rows'; label: string; value: number; max: number };

type ChosenFileCardProps = {
    fileName: string;
    /** The line under the name; `null` shows none. */
    status: string | null;
    /** Cancel while the file is read or checked, Other file once it is. */
    action: { label: string; testId: string; onPress: () => void };
    progress: ChosenFileProgress | null;
    /**
     * A file dropped over the card, to replace this one; without it the card
     * takes no drop (while the file is read or checked).
     */
    onDrop?: (event: { items: DroppedItem[] }) => void;
    /** The file's alerts (the step's footer sits under the card). */
    children?: React.ReactNode;
};

// As on the drop zone: a dragged file often has no usable media type, so
// every drop is taken and the step's intake refuses what is not a workbook.
const getDropOperation = () => 'copy' as const;

/**
 * The accepted file in place of the drop zone: its name and where it stands,
 * the one action that drops it, a progress bar while it is read or checked,
 * and below, whatever the step has to say about it. With `onDrop` a file
 * can be dropped over it (react-aria's `useDrop`, no drop button: Other
 * file is the keyboard's way); the card turns `secondary` while one is
 * dragged over it.
 */
export const ChosenFileCard: React.FC<ChosenFileCardProps> = ({
    fileName,
    status,
    action,
    progress,
    onDrop,
    children,
}) => {
    const dropTarget = React.useRef<HTMLDivElement>(null);
    const { dropProps, isDropTarget } = useDrop({
        ref: dropTarget,
        isDisabled: onDrop === undefined,
        getDropOperation,
        onDrop: (event) => onDrop?.({ items: event.items }),
    });

    return (
        <div ref={dropTarget} {...dropProps}>
            <Card
                className="gap-4"
                variant={isDropTarget ? 'secondary' : 'default'}
                {...getNodeTestIdProp('catalog-import-chosen-file')}
            >
                <div className="flex items-center gap-3">
                    <Icon
                        aria-hidden
                        className="size-6 shrink-0 text-muted"
                        icon="gravity-ui:file"
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                        <Typography
                            truncate
                            weight="medium"
                            {...getNodeTestIdProp(
                                'catalog-import-chosen-file-name',
                            )}
                        >
                            {fileName}
                        </Typography>
                        {status === null ? null : (
                            <Typography
                                color="muted"
                                type="body-xs"
                                {...getNodeTestIdProp(
                                    'catalog-import-chosen-file-status',
                                )}
                            >
                                {status}
                            </Typography>
                        )}
                    </div>
                    <Button
                        onPress={action.onPress}
                        size="sm"
                        variant="ghost"
                        {...getNodeTestIdProp(action.testId)}
                    >
                        {action.label}
                    </Button>
                </div>
                {progress === null ? null : (
                    <ProgressBar
                        aria-label={progress.label}
                        isIndeterminate={progress.kind === 'indeterminate'}
                        maxValue={
                            progress.kind === 'rows' ? progress.max : undefined
                        }
                        size="sm"
                        value={
                            progress.kind === 'rows'
                                ? progress.value
                                : undefined
                        }
                        {...getNodeTestIdProp('catalog-import-upload-progress')}
                    >
                        <ProgressBar.Track>
                            <ProgressBar.Fill />
                        </ProgressBar.Track>
                    </ProgressBar>
                )}
                {children}
            </Card>
        </div>
    );
};
